import {
  type GetFlagFn,
  getMergedPlanEnablement,
  isProductEnabledInPlanEnablement,
  PLAN_ENABLEMENT_FLAG_NAMES,
  PLAN_ENABLEMENT_FLAG_NAMES_LIST,
  type RawMarketplaceCatalogLdValue,
} from '@gm-commercial/entitlements-client';

import { ProductCodes } from '~/common/models/plans.model';

/**
 * True when BPS marketplace enablement lists either {@link ProductCodes.OST} or
 * {@link ProductCodes.OSOF} with a non-empty definition:
 * legacy `productCode[]` shape, or catalog `Record<productCode, featureCode[]>` with at least one feature.
 */
export function isOstOrOsofProductListedInBpsMarketplaceEnablement(
  getFlag: GetFlagFn
): boolean {
  const value = getFlag(
    PLAN_ENABLEMENT_FLAG_NAMES.BPS_MARKETPLACE
  ) as RawMarketplaceCatalogLdValue;
  if (value == null) {
    return false;
  }
  if (Array.isArray(value)) {
    return (
      value.includes(ProductCodes.OST) || value.includes(ProductCodes.OSOF)
    );
  }
  if (typeof value === 'object') {
    const ostFeatures = (value as Record<string, unknown>)[ProductCodes.OST];
    const osofFeatures = (value as Record<string, unknown>)[ProductCodes.OSOF];
    return (
      (Array.isArray(ostFeatures) && ostFeatures.length > 0) ||
      (Array.isArray(osofFeatures) && osofFeatures.length > 0)
    );
  }
  return false;
}

export type ResolveShowTelematicsFleetNavSurfacesArgs = {
  getFlag: GetFlagFn;
  /**
   * Composite telematics entitlement gate supplied by callers.
   *
   * Name is preserved for compatibility, but callers currently pass
   * `hasProduct(ProductCodes.OST) || hasProduct(ProductCodes.OSOF)`.
   * This represents org product entitlement from API payload and plan-side
   * enablement as evaluated in the calling hooks.
   */
  hasTelematicsProductPlan: boolean;
};

/**
 * Whether fleet-shell telematics nav surfaces should show (map, safety, insights in fleet nav;
 * account items that reuse the same boolean, e.g. camera settings and notification Safety & Security).
 *
 * **Plan dominates:** If merged plan LD enablement enables neither {@link ProductCodes.OST}
 * nor {@link ProductCodes.OSOF},
 * returns `false`.
 *
 * **Fully entitled:** If the plan enables {@link ProductCodes.OST} or {@link ProductCodes.OSOF}
 * and `hasTelematicsProductPlan` is `true` (for example, OST-only or OSOF-only entitlement),
 * returns `true`.
 * BPS marketplace listing is not required in this branch.
 *
 * **Mixed-plan / discovery:** If the plan enables {@link ProductCodes.OST} or
 * {@link ProductCodes.OSOF} but `hasTelematicsProductPlan` is `false`, returns
 * `true` only when {@link isOstOrOsofProductListedInBpsMarketplaceEnablement} is `true` so destinations
 * can show enrollment UX. If marketplace lists neither OST nor OSOF, returns `false`.
 *
 * Otherwise returns `false`.
 */
export function resolveShowTelematicsFleetNavSurfaces({
  getFlag,
  hasTelematicsProductPlan,
}: ResolveShowTelematicsFleetNavSurfacesArgs): boolean {
  const mergedPlanEnablement = getMergedPlanEnablement(
    getFlag,
    PLAN_ENABLEMENT_FLAG_NAMES_LIST
  );
  if (
    !isProductEnabledInPlanEnablement(mergedPlanEnablement, ProductCodes.OST) &&
    !isProductEnabledInPlanEnablement(mergedPlanEnablement, ProductCodes.OSOF)
  ) {
    return false;
  }
  if (hasTelematicsProductPlan) {
    return true;
  }
  return isOstOrOsofProductListedInBpsMarketplaceEnablement(getFlag);
}
